/**
 * TeachBud Theme Engine
 *
 * ARCHITECTURE: Content and design are completely separate.
 *
 *   LessonBlueprint (source of truth)
 *           │
 *           ▼
 *   Instructional Content
 *           │
 *     ┌─────┴──────┬──────────────┐
 *     ▼            ▼              ▼
 * Lesson Theme  Activity Theme  Presentation Theme
 *     │            │              │
 *     ▼            ▼              ▼
 * Beautiful    Beautiful PDF   Beautiful Slides
 *  Lesson
 *
 * Themes only affect APPEARANCE — never instructional content.
 * A new theme is a new renderer of the same LessonBlueprint.
 */

// ── Theme IDs ─────────────────────────────────────────────────────────────────

export type TeachBudThemeId =
  | 'clean'
  | 'modern'
  | 'elementary'
  | 'middle-school'
  | 'high-school'
  | 'state-test'
  | 'notebook'
  | 'worksheet'
  | 'chalkboard'
  | 'contemporary'
  | 'fun'
  | 'elegant';

// ── SlideTheme (shared with presentation-engine) ──────────────────────────────

export interface SlideTheme {
  bg: string;
  heading: string;
  body: string;
  accent: string;
  accentBg: string;
  badge: string;
  border: string;
  overlay: string;
  pptxBg: string;
  pptxHeading: string;
  pptxBody: string;
}

// ── TeachBudTheme ─────────────────────────────────────────────────────────────

export interface TeachBudTheme {
  id: TeachBudThemeId;
  name: string;
  description: string;
  emoji: string;
  /** Two hex colors shown as a gradient swatch in the picker. */
  swatch: [string, string];

  // ── Document surface (hex) ─────────────────────────────────────────────────
  pageBg: string;       // outer container / page background
  cardBg: string;       // card / section surface
  cardBorder: string;   // card border

  // ── Typography (hex) ──────────────────────────────────────────────────────
  headingColor: string; // section/card headings
  bodyColor: string;    // body text
  mutedColor: string;   // muted / secondary text

  // ── Section accents (hex) — cycle through these for sections ─────────────
  sectionAccents: string[];

  // ── Header / cover area (hex) ─────────────────────────────────────────────
  headerBg1: string;    // gradient start
  headerBg2: string;    // gradient end
  headerText: string;   // text on header

  // ── Interactive accent (hex) ──────────────────────────────────────────────
  accentColor: string;  // buttons, badges, links
  accentBg: string;     // light tint background for badges
  accentText: string;   // text color on accentBg

  // ── Presentation slides ───────────────────────────────────────────────────
  slide: SlideTheme;
}

// ── Theme Registry ────────────────────────────────────────────────────────────

export const TEACHBUD_THEMES: Record<TeachBudThemeId, TeachBudTheme> = {
  clean: {
    id: 'clean',
    name: 'Clean / Minimal',
    description: 'Simple, distraction-free layout. Works for all grade levels.',
    emoji: '⬜',
    swatch: ['#ffffff', '#94a3b8'],
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    headingColor: '#0f172a',
    bodyColor: '#334155',
    mutedColor: '#94a3b8',
    sectionAccents: ['#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8'],
    headerBg1: '#1e293b',
    headerBg2: '#334155',
    headerText: '#ffffff',
    accentColor: '#475569',
    accentBg: '#f1f5f9',
    accentText: '#1e293b',
    slide: {
      bg: 'bg-white',
      heading: 'text-slate-900',
      body: 'text-slate-600',
      accent: 'text-slate-800',
      accentBg: 'bg-slate-100',
      badge: 'bg-slate-100 text-slate-600',
      border: 'border-slate-200',
      overlay: 'bg-white/90',
      pptxBg: 'FFFFFF',
      pptxHeading: '1e293b',
      pptxBody: '475569',
    },
  },

  modern: {
    id: 'modern',
    name: 'Modern Professional',
    description: 'Clean lines with a blue accent. Suitable for any classroom.',
    emoji: '🔵',
    swatch: ['#2563eb', '#3b82f6'],
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#bfdbfe',
    headingColor: '#0f172a',
    bodyColor: '#334155',
    mutedColor: '#94a3b8',
    sectionAccents: ['#22c55e', '#94a3b8', '#6366f1', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#f97316', '#f43f5e', '#06b6d4', '#a855f7', '#14b8a6'],
    headerBg1: '#1d4ed8',
    headerBg2: '#2563eb',
    headerText: '#ffffff',
    accentColor: '#2563eb',
    accentBg: '#eff6ff',
    accentText: '#1d4ed8',
    slide: {
      bg: 'bg-white',
      heading: 'text-slate-900',
      body: 'text-slate-600',
      accent: 'text-blue-600',
      accentBg: 'bg-blue-50',
      badge: 'bg-blue-100 text-blue-700',
      border: 'border-blue-200',
      overlay: 'bg-white/90',
      pptxBg: 'FFFFFF',
      pptxHeading: '1e293b',
      pptxBody: '2563eb',
    },
  },

  elementary: {
    id: 'elementary',
    name: 'Elementary Colorful',
    description: 'Bright, inviting colors. Perfect for K–5 classrooms.',
    emoji: '🌈',
    swatch: ['#f59e0b', '#f97316'],
    pageBg: '#fffbeb',
    cardBg: '#fff7ed',
    cardBorder: '#fed7aa',
    headingColor: '#7c2d12',
    bodyColor: '#9a3412',
    mutedColor: '#c2410c',
    sectionAccents: ['#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'],
    headerBg1: '#f59e0b',
    headerBg2: '#f97316',
    headerText: '#ffffff',
    accentColor: '#f97316',
    accentBg: '#fff7ed',
    accentText: '#7c2d12',
    slide: {
      bg: 'bg-amber-50',
      heading: 'text-orange-800',
      body: 'text-amber-900',
      accent: 'text-orange-500',
      accentBg: 'bg-orange-100',
      badge: 'bg-orange-200 text-orange-700',
      border: 'border-orange-200',
      overlay: 'bg-amber-50/90',
      pptxBg: 'fffbeb',
      pptxHeading: '9a3412',
      pptxBody: '92400e',
    },
  },

  'middle-school': {
    id: 'middle-school',
    name: 'Middle School',
    description: 'Purple and violet tones. Works well for grades 6–8.',
    emoji: '💜',
    swatch: ['#7c3aed', '#8b5cf6'],
    pageBg: '#f5f3ff',
    cardBg: '#faf5ff',
    cardBorder: '#ddd6fe',
    headingColor: '#2e1065',
    bodyColor: '#4c1d95',
    mutedColor: '#7c3aed',
    sectionAccents: ['#7c3aed', '#db2777', '#2563eb', '#059669', '#d97706', '#dc2626'],
    headerBg1: '#6d28d9',
    headerBg2: '#7c3aed',
    headerText: '#ffffff',
    accentColor: '#7c3aed',
    accentBg: '#f5f3ff',
    accentText: '#4c1d95',
    slide: {
      bg: 'bg-violet-50',
      heading: 'text-violet-900',
      body: 'text-violet-700',
      accent: 'text-violet-600',
      accentBg: 'bg-violet-100',
      badge: 'bg-violet-200 text-violet-700',
      border: 'border-violet-200',
      overlay: 'bg-violet-50/90',
      pptxBg: 'f5f3ff',
      pptxHeading: '4c1d95',
      pptxBody: '5b21b6',
    },
  },

  'high-school': {
    id: 'high-school',
    name: 'High School',
    description: 'Sophisticated indigo. Appropriate for grades 9–12.',
    emoji: '🎓',
    swatch: ['#312e81', '#4338ca'],
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#c7d2fe',
    headingColor: '#1e1b4b',
    bodyColor: '#334155',
    mutedColor: '#6b7280',
    sectionAccents: ['#4338ca', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed'],
    headerBg1: '#312e81',
    headerBg2: '#4338ca',
    headerText: '#ffffff',
    accentColor: '#4338ca',
    accentBg: '#eef2ff',
    accentText: '#1e1b4b',
    slide: {
      bg: 'bg-slate-50',
      heading: 'text-indigo-900',
      body: 'text-slate-600',
      accent: 'text-indigo-600',
      accentBg: 'bg-indigo-50',
      badge: 'bg-indigo-100 text-indigo-700',
      border: 'border-indigo-200',
      overlay: 'bg-slate-50/90',
      pptxBg: 'f8fafc',
      pptxHeading: '1e1b4b',
      pptxBody: '4338ca',
    },
  },

  'state-test': {
    id: 'state-test',
    name: 'State-Test Style',
    description: 'Standardized look matching official test formats.',
    emoji: '📋',
    swatch: ['#ffffff', '#1e293b'],
    pageBg: '#ffffff',
    cardBg: '#ffffff',
    cardBorder: '#cbd5e1',
    headingColor: '#0f172a',
    bodyColor: '#1e293b',
    mutedColor: '#64748b',
    sectionAccents: ['#0f172a', '#0f172a', '#0f172a', '#0f172a', '#0f172a', '#0f172a'],
    headerBg1: '#0f172a',
    headerBg2: '#1e293b',
    headerText: '#ffffff',
    accentColor: '#0f172a',
    accentBg: '#f1f5f9',
    accentText: '#0f172a',
    slide: {
      bg: 'bg-white',
      heading: 'text-slate-900',
      body: 'text-slate-800',
      accent: 'text-slate-900',
      accentBg: 'bg-slate-50',
      badge: 'bg-slate-200 text-slate-700',
      border: 'border-slate-300',
      overlay: 'bg-white/95',
      pptxBg: 'FFFFFF',
      pptxHeading: '0f172a',
      pptxBody: '1e293b',
    },
  },

  notebook: {
    id: 'notebook',
    name: 'Notebook Style',
    description: 'Warm cream with a handwritten feel. Cozy and approachable.',
    emoji: '📓',
    swatch: ['#fef9c3', '#3b82f6'],
    pageBg: '#fefce8',
    cardBg: '#fef9c3',
    cardBorder: '#fde047',
    headingColor: '#1c1917',
    bodyColor: '#292524',
    mutedColor: '#78716c',
    sectionAccents: ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'],
    headerBg1: '#713f12',
    headerBg2: '#92400e',
    headerText: '#fef9c3',
    accentColor: '#3b82f6',
    accentBg: '#eff6ff',
    accentText: '#1d4ed8',
    slide: {
      bg: 'bg-yellow-50',
      heading: 'text-stone-900',
      body: 'text-stone-700',
      accent: 'text-blue-600',
      accentBg: 'bg-blue-50',
      badge: 'bg-blue-100 text-blue-700',
      border: 'border-yellow-300',
      overlay: 'bg-yellow-50/90',
      pptxBg: 'fefce8',
      pptxHeading: '1c1917',
      pptxBody: '3b82f6',
    },
  },

  worksheet: {
    id: 'worksheet',
    name: 'Worksheet Style',
    description: 'Classic lined-paper look. Great for printable handouts.',
    emoji: '📄',
    swatch: ['#f1f5f9', '#475569'],
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    headingColor: '#0f172a',
    bodyColor: '#1e293b',
    mutedColor: '#64748b',
    sectionAccents: ['#475569', '#94a3b8', '#64748b', '#334155', '#1e293b', '#0f172a'],
    headerBg1: '#475569',
    headerBg2: '#64748b',
    headerText: '#ffffff',
    accentColor: '#475569',
    accentBg: '#f1f5f9',
    accentText: '#0f172a',
    slide: {
      bg: 'bg-slate-50',
      heading: 'text-slate-900',
      body: 'text-slate-700',
      accent: 'text-slate-600',
      accentBg: 'bg-slate-100',
      badge: 'bg-slate-200 text-slate-600',
      border: 'border-slate-300',
      overlay: 'bg-slate-50/90',
      pptxBg: 'f8fafc',
      pptxHeading: '0f172a',
      pptxBody: '475569',
    },
  },

  chalkboard: {
    id: 'chalkboard',
    name: 'Chalkboard',
    description: 'Dark background with light text. Dramatic and engaging.',
    emoji: '🖊️',
    swatch: ['#1c2a1c', '#4ade80'],
    pageBg: '#1a2e1a',
    cardBg: '#1e3a1e',
    cardBorder: '#166534',
    headingColor: '#f0fdf4',
    bodyColor: '#dcfce7',
    mutedColor: '#86efac',
    sectionAccents: ['#4ade80', '#facc15', '#60a5fa', '#f87171', '#c084fc', '#34d399'],
    headerBg1: '#14532d',
    headerBg2: '#166534',
    headerText: '#f0fdf4',
    accentColor: '#4ade80',
    accentBg: '#14532d',
    accentText: '#f0fdf4',
    slide: {
      bg: 'bg-slate-900',
      heading: 'text-white',
      body: 'text-slate-300',
      accent: 'text-cyan-400',
      accentBg: 'bg-slate-800',
      badge: 'bg-slate-700 text-slate-300',
      border: 'border-slate-700',
      overlay: 'bg-slate-900/90',
      pptxBg: '0f172a',
      pptxHeading: 'ffffff',
      pptxBody: '94a3b8',
    },
  },

  contemporary: {
    id: 'contemporary',
    name: 'Contemporary Classroom',
    description: 'Teal and emerald. Modern, fresh, and versatile.',
    emoji: '🌿',
    swatch: ['#0d9488', '#059669'],
    pageBg: '#f0fdfa',
    cardBg: '#ffffff',
    cardBorder: '#99f6e4',
    headingColor: '#0f172a',
    bodyColor: '#1e293b',
    mutedColor: '#64748b',
    sectionAccents: ['#0d9488', '#059669', '#0284c7', '#7c3aed', '#d97706', '#dc2626'],
    headerBg1: '#0f766e',
    headerBg2: '#0d9488',
    headerText: '#ffffff',
    accentColor: '#0d9488',
    accentBg: '#f0fdfa',
    accentText: '#134e4a',
    slide: {
      bg: 'bg-teal-50',
      heading: 'text-teal-900',
      body: 'text-slate-700',
      accent: 'text-teal-600',
      accentBg: 'bg-teal-50',
      badge: 'bg-teal-100 text-teal-700',
      border: 'border-teal-200',
      overlay: 'bg-teal-50/90',
      pptxBg: 'f0fdfa',
      pptxHeading: '134e4a',
      pptxBody: '0d9488',
    },
  },

  fun: {
    id: 'fun',
    name: 'Fun / Cartoon',
    description: 'Bold colors and high energy. Kids love it.',
    emoji: '🎉',
    swatch: ['#ec4899', '#f59e0b'],
    pageBg: '#fdf4ff',
    cardBg: '#ffffff',
    cardBorder: '#f0abfc',
    headingColor: '#701a75',
    bodyColor: '#4a044e',
    mutedColor: '#a21caf',
    sectionAccents: ['#ec4899', '#f59e0b', '#3b82f6', '#10b981', '#f43f5e', '#6366f1'],
    headerBg1: '#ec4899',
    headerBg2: '#f59e0b',
    headerText: '#ffffff',
    accentColor: '#ec4899',
    accentBg: '#fdf4ff',
    accentText: '#701a75',
    slide: {
      bg: 'bg-fuchsia-50',
      heading: 'text-fuchsia-900',
      body: 'text-purple-800',
      accent: 'text-pink-500',
      accentBg: 'bg-pink-50',
      badge: 'bg-pink-100 text-pink-700',
      border: 'border-pink-200',
      overlay: 'bg-fuchsia-50/90',
      pptxBg: 'fdf4ff',
      pptxHeading: '701a75',
      pptxBody: 'ec4899',
    },
  },

  elegant: {
    id: 'elegant',
    name: 'Elegant',
    description: 'Navy and gold. Professional and refined.',
    emoji: '✨',
    swatch: ['#1e3a5f', '#b45309'],
    pageBg: '#f8f6f0',
    cardBg: '#ffffff',
    cardBorder: '#d9c99a',
    headingColor: '#1e3a5f',
    bodyColor: '#1e293b',
    mutedColor: '#64748b',
    sectionAccents: ['#b45309', '#1e3a5f', '#0f766e', '#7c3aed', '#be123c', '#1d4ed8'],
    headerBg1: '#1e3a5f',
    headerBg2: '#1e3a8a',
    headerText: '#fef3c7',
    accentColor: '#b45309',
    accentBg: '#fefce8',
    accentText: '#78350f',
    slide: {
      bg: 'bg-stone-50',
      heading: 'text-blue-950',
      body: 'text-slate-700',
      accent: 'text-amber-700',
      accentBg: 'bg-amber-50',
      badge: 'bg-amber-100 text-amber-800',
      border: 'border-amber-200',
      overlay: 'bg-stone-50/90',
      pptxBg: 'f8f6f0',
      pptxHeading: '1e3a5f',
      pptxBody: 'b45309',
    },
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

export function getTheme(id: TeachBudThemeId): TeachBudTheme {
  return TEACHBUD_THEMES[id] ?? TEACHBUD_THEMES.modern;
}

/** Returns sectionAccents cycling for an index (handles > 6 sections). */
export function sectionAccent(theme: TeachBudTheme, index: number): string {
  return theme.sectionAccents[index % theme.sectionAccents.length];
}

export const THEME_LIST = Object.values(TEACHBUD_THEMES);
